import { useEffect, useRef, useState } from 'react';
import { isErrorEnvelope } from '@/api/envelope';
import { MAX_TAKEOUT_BYTES, useInboundTakeout } from '@/api/hooks/useInboundTakeout';

function takeoutErrorCode(error: unknown): { status?: number; code?: string } {
  if (typeof error === 'string') return { code: error };
  if (!error || typeof error !== 'object') return {};
  if ('code' in error && typeof (error as { code?: unknown }).code === 'string') {
    return { code: (error as { code: string }).code };
  }
  if (!('response' in error)) return {};
  const response = (error as { response?: { status?: number; data?: unknown } }).response;
  return {
    status: response?.status,
    code: isErrorEnvelope(response?.data) ? response.data.error.code : undefined,
  };
}

export function takeoutUploadErrorCopy(error: unknown): string {
  const { status, code } = takeoutErrorCode(error);
  if (status === 413 || code === 'too_large') {
    return 'This zip is larger than MoneySkyline can accept here (90 MB). Keep the file; a smaller orders-only archive is required.';
  }
  if (code === 'wrong_archive_structure') {
    return 'This zip must contain only Takeout/Mail/orders.mbox. A full Google Takeout dump cannot be imported.';
  }
  if (code === 'corrupt_zip' || code === 'encrypted_zip' || code === 'unsafe_compression') {
    return 'This zip could not be read. Choose a corrected orders archive.';
  }
  if (code === 'active_import') {
    return 'A Takeout import is already running.';
  }
  return 'The upload could not be completed. Check the zip and try again.';
}

const STORED_COUNT_LABELS = [
  ['accepted_messages', 'Stored emails'],
  ['duplicate_messages', 'Already imported'],
  ['rejected_messages', 'Could not be stored'],
] as const;

export function takeoutCountLines(
  counts: Record<string, number>,
  status: 'queued' | 'importing' | 'calibrating' | 'complete' | 'failed',
): string[] {
  if (status === 'queued' || status === 'failed') return [];
  const lines: string[] = [];
  if (status === 'complete' && (counts.accepted_messages ?? 0) === 0 && (counts.duplicate_messages ?? 0) > 0) {
    lines.push('No new mail was stored. These messages were already imported.');
  }
  for (const [key, label] of STORED_COUNT_LABELS) {
    if (!(key in counts)) continue;
    const value = counts[key];
    if (status !== 'complete' && value === 0) continue;
    lines.push(`${label}: ${value}`);
  }
  if (status === 'complete' && (counts.unparsed_messages ?? 0) > 0) {
    lines.push('Some stored messages were not usable as matchable receipts.');
  }
  return lines;
}

export function TakeoutUploadSection() {
  const takeout = useInboundTakeout();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [oversize, setOversize] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const terminalAlert = useRef<HTMLParagraphElement>(null);
  const takeoutStatus = takeout.status.data;
  const activeTakeout = Boolean(takeoutStatus && ['queued', 'importing', 'calibrating'].includes(takeoutStatus.status));

  useEffect(() => {
    if (takeoutStatus?.status === 'complete' || takeoutStatus?.status === 'failed') terminalAlert.current?.focus();
  }, [takeoutStatus?.status]);

  const chooseTakeout = (file: File | undefined) => {
    if (!file) return;
    setSelectedFile(file);
    setOversize(file.size > MAX_TAKEOUT_BYTES);
    setUploadProgress(null);
  };
  const uploadTakeout = () => {
    if (!selectedFile || oversize || activeTakeout) return;
    setUploadProgress(0);
    takeout.upload.mutate({ file: selectedFile, onProgress: setUploadProgress });
  };
  const statusMessage = takeoutStatus?.status === 'importing'
    ? 'Importing messages.'
    : takeoutStatus?.status === 'calibrating'
      ? 'Messages stored. Reading receipts.'
      : takeoutStatus?.status === 'complete'
        ? 'Takeout import complete.'
        : takeoutStatus?.status === 'queued'
          ? 'Upload received. Import waiting to start.'
          : null;

  return (
    <section className="space-y-3 rounded-lg border border-border p-4 sm:p-6" aria-labelledby="receipt-takeout-heading">
      <h2 id="receipt-takeout-heading" className="text-lg font-semibold">Optional Takeout upload</h2>
      <p className="text-sm text-muted-foreground">
        Upload a Google Takeout zip that contains only <code>Takeout/Mail/orders.mbox</code> and is 90 MB or smaller. Bank transactions stay the money record.
      </p>
      <div className="flex flex-col items-start gap-3">
        <label htmlFor="takeout-orders-zip" className="text-sm font-medium">Google Takeout orders zip</label>
        <input
          id="takeout-orders-zip"
          name="archive"
          type="file"
          accept=".zip,application/zip"
          onChange={(event) => chooseTakeout(event.target.files?.[0])}
          disabled={activeTakeout || takeout.upload.isPending}
          className="ms-tap-44 block min-h-11 max-w-full text-sm"
          aria-describedby="takeout-upload-help"
        />
        <button
          type="button"
          onClick={uploadTakeout}
          disabled={!selectedFile || oversize || activeTakeout || takeout.upload.isPending}
          className="ms-tap-44 inline-flex min-h-11 items-center rounded-md bg-primary px-4 text-sm text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
        >
          {takeout.upload.isPending ? 'Uploading…' : 'Upload Takeout zip'}
        </button>
      </div>
      <p id="takeout-upload-help" className="text-sm text-muted-foreground" aria-live="polite">
        {oversize
          ? 'This zip is larger than 90 MB, so MoneySkyline did not upload it. Keep the file; larger Takeout archives are not supported yet.'
          : selectedFile
            ? `${selectedFile.name} is ready to upload.`
            : 'Choose the orders zip to begin.'}
      </p>
      {takeout.upload.isPending && uploadProgress !== null && (
        <p role="status" aria-live="polite" className="text-sm">Uploading {uploadProgress}%.</p>
      )}
      {statusMessage && <p role="status" aria-live="polite" className="text-sm">{statusMessage}</p>}
      {takeoutStatus?.progress && takeoutStatus.status !== 'queued' && (
        <p className="text-sm">Imported {takeoutStatus.progress.processed_messages} of {takeoutStatus.progress.total_messages} messages.</p>
      )}
      {takeoutStatus && takeoutCountLines(takeoutStatus.counts, takeoutStatus.status).map((line) => (
        <p key={line} className="text-sm">{line}</p>
      ))}
      {takeoutStatus?.status === 'failed' && (
        <p ref={terminalAlert} tabIndex={-1} role="alert" className="text-sm">
          {takeoutUploadErrorCopy(takeoutStatus.error)}{' '}
          {takeoutStatus.error?.retry_same_archive ? 'You can retry this archive.' : 'Choose a corrected zip and try again.'}
        </p>
      )}
      {takeoutStatus?.status === 'complete' && (
        <p ref={terminalAlert} tabIndex={-1} role="alert" className="text-sm">Takeout upload finished.</p>
      )}
      {takeout.upload.isError && (
        <p role="alert" className="text-sm">{takeoutUploadErrorCopy(takeout.upload.error)}</p>
      )}
    </section>
  );
}
